import {
  Box as MuiBox,
  Typography as MuiTypography,
  Grid as MuiGrid,
  Skeleton as MuiSkeleton,
} from "@mui/material";
import { Leaderboard as MuiLeaderboardIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAlerts } from "common";
import axios from "axios";
import { useNavBar } from "app/shared/services/NavigationService/NavigationProvider";
import { getStudyTeamNavigation } from "app/services/studiesService";
import PageContent from "app/shared/UI/Layout/PageContent";
import { GET_STUDIES_URL_BASE } from "app/shared/constants";
import WebsiteActivityCard from "./WebsiteActivityCard";
import ListingStatusCard from "./ListingStatusCard";
import ParticipantEngagementCard from "./ParticipantEngagementCard";
import HonestBrokerCard from "./HonestBrokerCard";

import StudyStatusCard from "./StudyStatusCard";
import AccrualProgressCard from "./AccrualProgressCard";
import StudyMetadataCard from "./StudyMetadataCard";

const StudyInsightsPageContent = () => {
  const { protocolNumber } = useParams();
  const { setAlert } = useAlerts();
  const { addNavItem, clearNavigation } = useNavBar();

  const [studyData, setStudyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!protocolNumber) return;

    const fetchStudy = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${GET_STUDIES_URL_BASE}/${protocolNumber}/insights`,
        );
        setStudyData(res.data);
      } catch (err) {
        console.error(err);
        if (!axios.isCancel(err)) {
          setAlert("error", err.message);
          clearNavigation();
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStudy();
  }, [protocolNumber, setAlert, clearNavigation]);

  useEffect(() => {
    const studyTeamNavItems = getStudyTeamNavigation(protocolNumber);
    studyTeamNavItems.forEach((navItem) => addNavItem(navItem));
    return () => {
      clearNavigation();
    };
  }, [protocolNumber, addNavItem, clearNavigation]);

  return (
    !error && (
      <MuiBox
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MuiTypography variant="h1" fontWeight={500} sx={{ px: 4, py: 3 }}>
          <MuiLeaderboardIcon sx={{ marginBottom: "-5px" }} />{" "}
          {`'${protocolNumber}' Insights`}
        </MuiTypography>

        <PageContent>
          <MuiGrid container spacing={4}>
            {/* Study Status */}
            <MuiGrid size={{ xs: 12, md: 4 }}>
              {!loading && (
                <StudyStatusCard
                  protocolStatus={studyData?.protocolStatus}
                  protocolSummaryAccrual={studyData?.protocolSummaryAccrual}
                />
              )}
              {loading && (
                <MuiSkeleton
                  variant="rectangular"
                  height={120}
                  sx={{ mb: 2 }}
                />
              )}
            </MuiGrid>

            {/* Accrual Progress */}
            <MuiGrid size={{ xs: 12, md: 4 }}>
              {!loading && (
                <AccrualProgressCard
                  enrolled={studyData?.subjectSummary?.enrolled}
                  target={studyData?.protocolTargetAccrual}
                  endDate={studyData?.protocolIrbExpirationDate}
                />
              )}
              {loading && (
                <MuiSkeleton
                  variant="rectangular"
                  height={120}
                  sx={{ mb: 2 }}
                />
              )}
            </MuiGrid>

            {/* Study Metadata */}
            <MuiGrid size={{ xs: 12, md: 4 }}>
              {!loading && (
                <StudyMetadataCard
                  irbNumber={studyData?.irbNumber}
                  nctId={studyData?.nctId}
                  protocolNumber={protocolNumber}
                  title={studyData?.protocolTitle}
                />
              )}
              {loading && (
                <MuiSkeleton
                  variant="rectangular"
                  height={120}
                  sx={{ mb: 2 }}
                />
              )}
            </MuiGrid>
          </MuiGrid>
          {/* Website activity */}
          <MuiGrid container spacing={4} mt={4}>
            <MuiGrid size={{ xs: 12, md: 8 }}>
              {!loading && (
                <WebsiteActivityCard
                  interestedCount={studyData?.responseSummary?.interested ?? 0}
                  interestCountFromCt={studyData?.enrolledCountFromCt ?? 0}
                  isPublishable={
                    [
                      "OPEN_TO_ACCRUAL",
                      "SUSPENDED",
                      "CLOSED_TO_ACCRUAL",
                      "TERMINATED",
                      "IRB_STUDY_CLOSURE",
                    ].includes(studyData?.protocolStatus) && studyData?.nctId
                  }
                />
              )}
              {loading && (
                <MuiSkeleton
                  variant="rectangular"
                  height={300}
                  sx={{ mb: 2 }}
                />
              )}
            </MuiGrid>
            <MuiGrid size={{ xs: 12, md: 4 }}>
              {!loading && (
                <ListingStatusCard
                  studyListingStatus={studyData?.studyListingStatus}
                  nctId={studyData?.nctId}
                  isPublishable={
                    [
                      "OPEN_TO_ACCRUAL",
                      "SUSPENDED",
                      "CLOSED_TO_ACCRUAL",
                      "TERMINATED",
                      "IRB_STUDY_CLOSURE",
                    ].includes(studyData?.protocolStatus) && studyData?.nctId
                  }
                />
              )}
              {loading && (
                <MuiSkeleton
                  variant="rectangular"
                  height={300}
                  sx={{ mb: 2 }}
                />
              )}
            </MuiGrid>
          </MuiGrid>

          {/* Participant engagement */}
          <MuiGrid container spacing={4} mt={4}>
            <MuiGrid size={{ xs: 12, md: 8 }}>
              {!loading && (
                <ParticipantEngagementCard
                  honestBrokerStatus={studyData?.honestBrokerStatus}
                  contactedCount={studyData?.outreachSummary?.sent ?? 0}
                  respondedCount={studyData?.responseSummary?.responded ?? 0}
                  positiveResponseCount={
                    studyData?.responseSummary?.interested ?? 0
                  }
                />
              )}
              {loading && (
                <MuiSkeleton
                  variant="rectangular"
                  height={300}
                  sx={{ mb: 2 }}
                />
              )}
            </MuiGrid>
            <MuiGrid size={{ xs: 12, md: 4 }}>
              {!loading && (
                <HonestBrokerCard
                  honestBrokerStatus={studyData?.honestBrokerStatus}
                />
              )}
              {loading && (
                <MuiSkeleton
                  variant="rectangular"
                  height={300}
                  sx={{ mb: 2 }}
                />
              )}
            </MuiGrid>
          </MuiGrid>
        </PageContent>

        <p>&nbsp;</p>
      </MuiBox>
    )
  );
};

export default StudyInsightsPageContent;
